import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { apiKey } from "../config/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";

export default function SellNewItem({ lang }) {
  const token = JSON.parse(sessionStorage.getItem("aman-admin") || "null")
    ?.admin
    ? JSON.parse(JSON.parse(sessionStorage.getItem("aman-admin")).admin).token
    : null;

  const navigate = useNavigate();

  const [branchId, setBranchId] = useState();

  // transaction type: 'sell' or 'buy'
  const [trxType, setTrxType] = useState("sell");

  // loading states
  const [investorLoading, setInvestorLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  // dropdowns
  const [investors, setInvestors] = useState([]);
  const [items, setItems] = useState([]);

  // selected
  const [investor, setInvestor] = useState("");
  const [item, setItem] = useState("");

  // auto fields (stock)
  const [unitPrice, setUnitPrice] = useState(0); // stock unit price (readonly)
  const [availableQty, setAvailableQty] = useState(0);

  // ✅ selling fields
  const [sellUnitPrice, setSellUnitPrice] = useState(""); // selling per-unit price (editable)
  const [sellQty, setSellQty] = useState("");

  // optional customer
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  const [itemId, setItemId] = useState(""); // backend item_id

  const location = useLocation();
  const { id: routeId } = useParams();

  const isEditMode = !!routeId;
  const editId = routeId;
  const reportData = location.state?.reportData;

  useEffect(() => {
    if (isEditMode && reportData) {
      setSellQty(reportData.quantity || 0);
      setSellUnitPrice(reportData.unit_price || 0);
      setCustomerName(reportData.customer_name || "");
      setCustomerPhone(reportData.phone_no || "");
      setInvestor(reportData.investor_name || ""); // Temporary for display
      setItem(reportData.inventory_item || ""); // Temporary for display
    }
  }, [isEditMode, reportData]);

  useEffect(() => {
    try {
      setBranchId(
        JSON.parse(JSON.parse(sessionStorage.getItem("aman-admin")).admin).branch_id
      );
    } catch (e) {
      console.error("Session parse error:", e);
    }
  }, []);

  // Find selected investor object to access balance
  const selectedInv = useMemo(() => {
    return investors.find((inv) => String(inv.inv_id || inv.id) === String(investor));
  }, [investors, investor]);

  // ✅ Total auto calculate (selling_unit_price * qty)
  const totalPrice = useMemo(() => {
    const up = parseFloat(String(sellUnitPrice).replace(/,/g, "")) || 0;
    const q = Number(sellQty) || 0;
    return up * q;
  }, [sellUnitPrice, sellQty]);

  // Balance validation for Buy mode
  const balanceExceed = useMemo(() => {
    if (isEditMode) return false;
    if (trxType !== "buy") return false;
    const bal = parseFloat(selectedInv?.balance || 0);
    return totalPrice > bal;
  }, [totalPrice, selectedInv, trxType, isEditMode]);

  // ✅ Load investors
  useEffect(() => {
    if (!branchId) return;

    setInvestorLoading(true);
    axios
      .get(apiKey + "/investor", {
        params: { branch_id: branchId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setInvestors(res.data.result || []))
      .catch((err) => {
        console.error(err);
        toast.error("Error loading investors");
      })
      .finally(() => setInvestorLoading(false));
  }, [branchId, token]);

  // ✅ when investor changes → load ONLY investor items (Sell mode)
  useEffect(() => {
    if (trxType !== "sell") return;
    if (!investor || !branchId) return;

    // reset dependent fields
    setItem("");
    setItemId("");
    setUnitPrice(0);
    setAvailableQty(0);
    setSellQty(0);
    setSellUnitPrice(0);

    setItemsLoading(true);

    axios
      .get(apiKey + "/get-investor-items", {
        params: {
          investor_id: investor,
          investorId: investor,
          branch_id: branchId,
          branchId: branchId,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const raw = res.data?.result || [];

        const list = raw.map((x) => ({
          inventory_id: x.inventory_id ?? x.id ?? x.inventoryId,
          item_id: x.item_id,
          inventory_item: x.inventory_item ?? x.inventory_name ?? x.name ?? "-",
          unit_price: x.unit_price ?? x.inventory_price ?? x.price ?? 0, // stock unit
          quantity: x.quantity ?? x.inventory_quantity ?? 0,
        }));

        setItems(list);
      })
      .catch((err) => {
        console.error("get-investor-items error:", err?.response || err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.response?.data?.error ||
          "Error loading investor items";
        toast.error(msg);
        setItems([]);
      })
      .finally(() => setItemsLoading(false));
  }, [investor, branchId, token, trxType]);

  // ✅ load system inventory items (Buy mode)
  useEffect(() => {
    if (trxType !== "buy" || !branchId) {
      if (trxType === "sell" && !investor) {
        setItems([]);
      }
      return;
    }

    // reset dependent fields
    setItem("");
    setItemId("");
    setUnitPrice(0);
    setAvailableQty(0);
    setSellQty(0);
    setSellUnitPrice(0);

    setItemsLoading(true);

    axios
      .get(`${apiKey}/get-system-inventory/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const raw = res.data?.result || [];
        const list = raw.map((x) => ({
          inventory_id: x.id,
          item_id: x.id,
          inventory_item: x.inventory_name || "-",
          unit_price: 0,
          quantity: 0,
        }));
        setItems(list);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error loading system items");
        setItems([]);
      })
      .finally(() => setItemsLoading(false));
  }, [trxType, branchId, token, investor]);

  const onSelectItem = (inventoryId) => {
    setItem(inventoryId);

    const selected = items.find(
      (i) => String(i.inventory_id) === String(inventoryId)
    );

    if (!selected) {
      setItemId("");
      setUnitPrice(0);
      setAvailableQty(0);
      setSellQty(0);
      setSellUnitPrice(0);
      return;
    }

    setItemId(selected.item_id);

    const stockUnit = Number(selected.unit_price) || 0;
    setUnitPrice(stockUnit);
    setAvailableQty(Number(selected.quantity) || 0);

    // ✅ default selling unit price = stock unit price (user can change)
    setSellUnitPrice(String(stockUnit || "")); // ✅ string
    setSellQty(0);
  };

  const qtyExceed = useMemo(() => {
    if (isEditMode) return false;
    if (trxType !== "sell") return false;
    return parseFloat(String(sellQty).replace(/,/g, "")) > Number(availableQty);
  }, [sellQty, availableQty, isEditMode, trxType]);

  const handleSubmit = () => {
    if (isEditMode) {
      axios
        .put(apiKey + `/update-sale/${editId}`, {
          quantity: parseFloat(String(sellQty).replace(/,/g, "")) || 0,
          price: Number(totalPrice) || 0,
          unit_price: parseFloat(sellUnitPrice) || 0,
          customer_name: customerName || null,
          phone_no: customerPhone || null,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then((res) => {
          toast.success(res.data?.Message || "Sale updated successfully");
          navigate(-1);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.Error || "Error updating sale");
        });
      return;
    }

    if (!investor) return toast.error("Please select investor");
    if (!item) return toast.error("Please select item");
    if (!itemId) return toast.error("Item id missing (check mapping)");
    if ((parseFloat(String(sellQty).replace(/,/g, "")) || 0) <= 0)
      return toast.error("Enter quantity");
    if ((Number(sellUnitPrice) || 0) <= 0)
      return toast.error("Enter unit price");
    if (trxType === "sell" && qtyExceed) return toast.error("Insufficient Quantity");
    if (trxType === "buy" && balanceExceed) return toast.error("Insufficient Investor Balance");

    const payload = {
      item_id: itemId,
      investor_id: investor,
      inventory_id: trxType === "buy" ? null : item,
      price: Number(totalPrice) || 0,
      unit_price: parseFloat(sellUnitPrice) || 0,
      customer_name: customerName || null,
      phone_no: customerPhone || null,
      quantity: parseFloat(String(sellQty).replace(/,/g, "")) || 0,
      branch_id: branchId,
      type: trxType,
    };

    axios
      .post(apiKey + "/sell-inventory-item", payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        toast.success(res.data?.message || "Inventory item sold successfully.");
        navigate(-1);
      })
      .catch((err) => {
        console.error("sell-inventory-item error:", err?.response || err);
        toast.error(
          err?.response?.data?.message ||
            "Error occurred while selling inventory item"
        );
      });
  };

  return (
    <main id="main" className="main">
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        theme="colored"
      />

      <div className="pagetitle">
        <h1>
          {lang.Investment_Loan_Management || "Investment/Loan Management"}
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
              >
                <path d="m21.743 12.331-9-10c-.379-.422-1.107-.422-1.486 0l-9 10a.998.998 0 0 0-.17 1.076c.16.361.518.593.913.593h2v7a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-4h4v4a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-7h2a.998.998 0 0 0 .743-1.669z"></path>
              </svg>
            </li>
            <li className="breadcrumb-item">
              {lang.Investment_Loan_Management || "Investment/Loan Management"}
            </li>
            <li className="breadcrumb-item active">
              {lang['Sell New Item'] || "Sell New Item"}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex mt-3 mb-3">
                  <h4 className="headingh4 fw-bold">
                    {isEditMode
                      ? lang.Edit_Sale || "Edit Sale"
                      : trxType === "sell"
                      ? lang.Sell_New_Item || "Sell New Item"
                      : lang.Buy_New_Item || "Buy New Item"}
                  </h4>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="d-flex mt-3 mb-3 justify-content-end">
                    <button
                      type="submit"
                      className="btn-save"
                      disabled={qtyExceed || balanceExceed}
                    >
                      {isEditMode ? lang.Save || "Save" : lang.Save_And_Next || "Save & Next"}
                    </button>
                  </div>

                  {!isEditMode && (
                    <div className="row mb-4 d-flex justify-content-center">
                      <div className="col-lg-4 text-center">
                        <div className="btn-group w-100" role="group">
                          <button
                            type="button"
                            className={`btn ${trxType === "sell" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                              setTrxType("sell");
                              setInvestor("");
                              setItem("");
                              setItemId("");
                              setUnitPrice(0);
                              setAvailableQty(0);
                              setSellQty(0);
                              setSellUnitPrice(0);
                            }}
                          >
                            {lang.sell_to_customer || "Sell to Customer"}
                          </button>
                          <button
                            type="button"
                            className={`btn ${trxType === "buy" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                              setTrxType("buy");
                              setInvestor("");
                              setItem("");
                              setItemId("");
                              setUnitPrice(0);
                              setAvailableQty(0);
                              setSellQty(0);
                              setSellUnitPrice(0);
                            }}
                          >
                            {lang.buy_from_customer || "Buy from Customer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {isEditMode ? (
                    // EDIT MODE READ-ONLY FIELDS
                    <>
                      <div className="row mb-3 d-flex justify-content-center">
                        <div className="col-lg-4">
                          <label className="w-100">
                            <span style={{ fontSize: "14px" }}>{lang.Investor || "Investor"}:</span>
                            <input type="text" className="form-control" value={reportData?.investor_name || ""} disabled />
                          </label>
                        </div>
                      </div>
                      <div className="row mb-3 d-flex justify-content-center">
                        <div className="col-lg-4">
                          <label className="w-100">
                            <span style={{ fontSize: "14px" }}>{lang.Item || "Item"}:</span>
                            <input type="text" className="form-control" value={reportData?.inventory_item || ""} disabled />
                          </label>
                        </div>
                      </div>
                    </>
                  ) : (
                    // CREATE MODE FIELDS
                    <>

                  {/* Investor */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Investor || "Investor"}:
                        </span>

                        <select
                          className="form-select"
                          value={investor}
                          onChange={(e) => setInvestor(e.target.value)}
                          required
                          disabled={investorLoading}
                        >
                          <option value="" disabled>
                            {investorLoading
                              ? lang.Loading || "Loading..."
                              : lang.Select_Investor || "Select Investor"}
                          </option>

                          {investors.map((inv) => (
                            <option key={inv.id} value={inv.id}>
                              {inv.FullName}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>

                  {/* Balance Visualization */}
                  {investor && (
                    <div className="row mb-3 d-flex justify-content-center">
                      <div className="col-lg-4">
                        <div className="p-2 rounded bg-light border d-flex justify-content-between align-items-center">
                          <span className="fw-bold" style={{ fontSize: "13px" }}>
                            {lang.Investor_Balance || "Investor Balance"}:
                          </span>
                          <span className="badge bg-primary fs-6">
                            {parseFloat(selectedInv?.balance || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Item */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Item || "Item"}:
                        </span>

                        <select
                          className="form-select"
                          value={item}
                          onChange={(e) => onSelectItem(e.target.value)}
                          required
                          disabled={!investor || itemsLoading}
                        >
                          <option value="" disabled>
                            {!investor
                              ? lang.Select_Investor_First ||
                                "Select investor first"
                              : itemsLoading
                              ? lang.Loading || "Loading..."
                              : lang.Select_Item || "Select Item"}
                          </option>

                          {items.map((it) => (
                            <option
                              key={it.inventory_id}
                              value={it.inventory_id}
                            >
                              {it.inventory_item}
                            </option>
                          ))}
                        </select>
                      </label>

                      {investor && !itemsLoading && items.length === 0 && (
                        <p className="bg-danger text-white text-center mt-2">
                          {lang.no_item_found ||
                            "No items found for this investor"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stock Unit Price (readonly) */}
                  {trxType === "sell" && (
                    <>
                      <div className="row mb-3 d-flex justify-content-center">
                        <div className="col-lg-4">
                          <label className="w-100">
                            <span style={{ fontSize: "14px" }}>
                              {lang.Unit_Price || "Stock Unit Price"}:
                            </span>
                            <input
                              className="form-control"
                              value={unitPrice}
                              disabled
                            />
                          </label>
                        </div>
                      </div>

                      {/* Available Qty */}
                      <div className="row mb-3 d-flex justify-content-center">
                        <div className="col-lg-4">
                          <label className="w-100">
                            <span style={{ fontSize: "14px" }}>
                              {lang.Available_Quantity || "Available Quantity"}:
                            </span>
                            <input
                              className="form-control"
                              value={availableQty}
                              disabled
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  </>
                  )}

                  {/* Selling Quantity */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Quantity || "Quantity"}:
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control"
                          value={sellQty}
                          onChange={(e) => {
                            // ✅ sirf digits allow (optional: decimal allow karna ho to adjust)
                            const val = e.target.value.replace(/[^\d]/g, "");
                            setSellQty(val);
                          }}
                          placeholder={lang.Enter_Quantity || "Enter quantity"}
                          required
                        />
                      </label>

                      {!isEditMode && item && trxType === "sell" && qtyExceed && (
                        <p className="bg-danger text-white text-center mt-2 p-1 rounded">
                          {lang.Insufficient_Quantity ||
                            "Insufficient Quantity"}
                        </p>
                      )}

                      {!isEditMode && investor && trxType === "buy" && balanceExceed && (
                        <p className="bg-danger text-white text-center mt-2 p-1 rounded">
                          {lang.Insufficient_Investor_Balance ||
                            "Insufficient Investor Balance"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ✅ Selling Unit Price (editable) */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Selling_Unit_Price || "Selling Unit Price"}:
                        </span>
                        <input
                          type="text" // ✅ text rakho to smooth typing
                          inputMode="numeric" // ✅ mobile numeric keypad
                          className="form-control"
                          value={sellUnitPrice}
                          onChange={(e) => {
                            // ✅ sirf digits allow (optional: decimal allow karna ho to adjust)
                            const val = e.target.value.replace(/[^\d.]/g, "");
                            setSellUnitPrice(val);
                          }}
                          placeholder={
                            lang.Enter_Selling_Unit_Price ||
                            "Enter selling unit price"
                          }
                          required
                        />
                      </label>
                    </div>
                  </div>

                  {/* Total Selling Price (selling_unit_price * qty) */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Total_Sell_price || "Total Selling Price"}:
                        </span>
                        <input
                          className="form-control"
                          value={totalPrice}
                          disabled
                        />
                      </label>
                    </div>
                  </div>

                  {/* Customer Name */}
                  <div className="row mb-3 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Customer_Name || "Customer Name"}:
                        </span>
                        <input
                          className="form-control"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder={lang.Optional || "Optional"}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Customer Phone */}
                  <div className="row mb-4 d-flex justify-content-center">
                    <div className="col-lg-4">
                      <label className="w-100">
                        <span style={{ fontSize: "14px" }}>
                          {lang.Customer_Phone || "Customer Phone"}:
                        </span>
                        <input
                          className="form-control"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          placeholder={lang.Optional || "Optional"}
                        />
                      </label>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
